import './wdyr';
import Axios from "axios";
import React, {useEffect, Fragment} from 'react';
import {Route, Switch, withRouter} from 'react-router-dom';
import Routes from './components/routing/Routes';
import getApiUrl from './config/api';

// Components
import Sidebar from './components/layout/Sidebar';
import Landing from './components/layout/Landing';
import Navbar from './components/layout/Navbar';
import Login from './components/auth/Login';
import PrivateRoute from './components/routing/PrivateRoute';

import {loadUser} from './actions/auth';
import {toggleSidebar, showSidebar} from './actions/sidebar';
import {showNavbar} from './actions/navbar';
import {loadActiveFirm, loadUserFirms, autoSelectFirstFirm} from './actions/activeFirm';
import setAuthToken from './utils/setAuthToken';
import { handleTokenExpiration, validateToken } from './utils/tokenUtils';

import './app.css';
import './custom.css';
import './styles/overrides.css';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'antd/dist/antd.min.css';
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Alert from './components/layout/Alert';
import TokenExpirationWarning from './components/auth/TokenExpirationWarning';

import PropTypes from "prop-types";
import {connect} from "react-redux";

// Set default axios configuration
Axios.defaults.baseURL = getApiUrl();
Axios.defaults.headers.common['Content-Type'] = 'application/json';
Axios.defaults.headers.common['Accept'] = 'application/json';

// Add response interceptor for better error handling
Axios.interceptors.response.use(
    response => response,
    error => {
        if (error.response) {
            // Log the error but don't expose it to the user
            console.error('API Error:', error.response);
            
            // Handle 401 unauthorized - token expired or invalid
            if (error.response.status === 401) {
                console.warn('Received 401 Unauthorized - handling token expiration');
                handleTokenExpiration();
            }
        }
        return Promise.reject(error);
    }
);

// Add request interceptor to validate token before each request
Axios.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token && !validateToken(token)) {
            // Token is expired, don't send the request
            // The validateToken function already handled the logout
            return Promise.reject({ 
                response: { status: 401 },
                message: 'Token expired'
            });
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

if (localStorage.token) {
    setAuthToken(localStorage.token);
}

const App = ({ loadUser, toggleSidebar, showSidebar, showNavbar, loadActiveFirm, loadUserFirms, autoSelectFirstFirm, auth: { isAuthenticated }, location }) => {
    useEffect(() => {
        // Check if token is expired on app load
        const token = localStorage.getItem('token');
        if (token && !validateToken(token)) {
            // Token is expired, handleTokenExpiration will clean up
            console.log('Token expired on app load, handling expiration');
        } else {
            loadUser();
            // Load active firm from localStorage immediately (synchronous)
            loadActiveFirm();
        }
    }, [loadUser, loadActiveFirm]);
    
    // Load user firms and auto-select first firm when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            // Load firms and auto-select first one if no firm is selected
            autoSelectFirstFirm();
        }
    }, [isAuthenticated, autoSelectFirstFirm]);

    // Show sidebar and navbar when user is authenticated
    useEffect(() => {
        if (isAuthenticated) {
            showSidebar();
            showNavbar();
        }
    }, [isAuthenticated, showSidebar, showNavbar]);

    const getPageClass = () => {
        const path = location.pathname;
        // Remove leading and trailing slashes and replace remaining slashes with dashes
        const cleanPath = path.replace(/^\/+|\/+$/g, '').replace(/\//g, '-');
        return `wrapper page-${cleanPath || 'home'}`;
    };

    return (
        <Fragment>
            <TokenExpirationWarning />
            <div className={getPageClass()}>
                <Sidebar/>
                {isAuthenticated && <Navbar toggleSideBar={toggleSidebar}/>}

                <div className="main mt-5">
                    <Switch>
                        <Route exact path="/" component={Landing}/>
                        <Route exact path="/login" component={Login} />
                        <PrivateRoute component={Routes}/>
                    </Switch>
                </div>
            </div>
            <Alert/>
            <ToastContainer autoClose={5000} limit={1}/>
        </Fragment>
    );
};

App.propTypes = {
    loadUser: PropTypes.func.isRequired,
    toggleSidebar: PropTypes.func.isRequired,
    showSidebar: PropTypes.func.isRequired,
    showNavbar: PropTypes.func.isRequired,
    loadActiveFirm: PropTypes.func.isRequired,
    loadUserFirms: PropTypes.func.isRequired,
    autoSelectFirstFirm: PropTypes.func.isRequired,
    auth: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
    auth: state.auth
});

export default connect(mapStateToProps, { loadUser, toggleSidebar, showSidebar, showNavbar, loadActiveFirm, loadUserFirms, autoSelectFirstFirm })(withRouter(App));

